import {
  Alert,
  Box,
  Button,
  Card,
  CardContent,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  Grid,
  LinearProgress,
  Link as MuiLink,
  Stack,
  Tooltip,
  Typography,
} from '@mui/material';
// eslint-disable-next-line no-restricted-imports -- batch units list is fully fetched up front (bounded by batch size); no server-side pagination needed
import { DataGridPro } from '@mui/x-data-grid-pro';
import React, { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Link as RouterLink, useParams } from 'react-router-dom';

import { cancelFirmwareUpgradeBatch } from '@sophocles/api/firmwareUpgrades';
import AppNav from '@sophocles/components/App/AppNav';
import TitleBar from '@sophocles/components/App/TitleBar';
import StatusChip from '@sophocles/components/StatusChip';
import GridStatusCell from '@sophocles/data-grids/components/cells/GridStatusCell';
import {
  BATCH_CANCEL_DISABLED,
  STATUS_COLOR,
  UNIT_STATUS,
} from '@sophocles/views/admin/engineering/firmwareUpgrades/firmwareUpgradeStatus';
import useUpgradeBatchSocket from '@sophocles/views/admin/engineering/firmwareUpgrades/useUpgradeBatchSocket';

const formatTs = ts => (ts ? new Date(ts).toLocaleString() : '');

// STATUS_COLOR (firmwareUpgradeStatus.js) predates the shared status-pill
// family palette and still speaks MUI Chip colors — translate here rather
// than widen that shared module for this one page.
const STATUS_COLOR_FAMILY = {
  success: 'ok',
  error: 'bad',
  warning: 'warning',
  info: 'info',
  secondary: 'attention',
  default: 'neutral',
};

const statusFamily = status => STATUS_COLOR_FAMILY[STATUS_COLOR[status] || 'default'];

const renderUnitStatus = ({ row }) => {
  // `status` is computed backend-side (services/firmware_upgrades.py
  // compute_log_status). Errors get a tooltip with the reason.
  const status = row.status || UNIT_STATUS.QUEUED;
  const chip = <GridStatusCell color={statusFamily(status)} label={status} />;
  if (status === UNIT_STATUS.FAILED && row.error_reason) {
    return (
      <Tooltip title={row.error_reason}>
        <span>{chip}</span>
      </Tooltip>
    );
  }
  return chip;
};

const customerOf = row => row?.site?.site_group?.customer ?? null;
const siteGroupOf = row => row?.site?.site_group ?? null;
const siteOf = row => row?.site ?? null;

function buildRenderSiteLink(t) {
  return function RenderSiteLink({ row }) {
    const site = siteOf(row);
    const sg = siteGroupOf(row);
    const cust = customerOf(row);
    const label = site?.name || (site?.id != null ? t('firmware.siteFallback', { id: site.id }) : '—');
    if (cust?.id && sg?.id && site?.id) {
      const href = `/c/${cust.id}/plant/${sg.id}/structure/${site.id}`;
      return (
        <MuiLink component={RouterLink} to={href} underline="hover" sx={{ fontWeight: 500 }}>
          {label}
        </MuiLink>
      );
    }
    return label;
  };
}

function getColumns(t) {
  return [
    {
      field: 'site',
      headerName: t('firmware.columnSite'),
      flex: 1,
      minWidth: 160,
      renderCell: buildRenderSiteLink(t),
      valueGetter: (_value, row) => siteOf(row)?.name || '',
    },
    {
      field: 'site_group',
      headerName: t('firmware.columnGroup'),
      flex: 1,
      minWidth: 140,
      valueGetter: (_value, row) => siteGroupOf(row)?.name || '',
    },
    {
      field: 'customer',
      headerName: t('firmware.columnCustomer'),
      flex: 1,
      minWidth: 140,
      valueGetter: (_value, row) => customerOf(row)?.name || '',
    },
    { field: 'iccid', headerName: t('firmware.columnIccid'), flex: 1, minWidth: 160 },
    { field: 'mac_address', headerName: t('firmware.columnMac'), flex: 1, minWidth: 140 },
    { field: 'status', headerName: t('general.status'), width: 130, renderCell: renderUnitStatus, sortable: false },
    {
      field: 'progress_pct',
      headerName: t('firmware.columnProgress'),
      width: 140,
      type: 'number',
      // Backend exposes `progress_reporting: false` for runs that can't
      // emit mid-flight progress (NON302M against a pre-V2 source — one
      // `sock.sendall` then a single `_update_log` at the end). Mid-
      // flight the numeric %% stays at 0 the whole time, so swap it for
      // an indeterminate bar instead of a stuck-at-0 determinate one.
      renderCell: ({ row, value }) => {
        const indeterminate = row.progress_reporting === false && row.status === UNIT_STATUS.UPGRADING;
        if (indeterminate) {
          return (
            <Tooltip title={t('firmware.progressNoReporting')}>
              <Box sx={{ width: '100%' }}>
                <LinearProgress sx={{ height: 6, borderRadius: 'var(--radius-md)' }} />
              </Box>
            </Tooltip>
          );
        }
        return value == null ? '' : `${value}%`;
      },
    },
    { field: 'programming_segment', headerName: t('firmware.columnSegment'), width: 100, type: 'number' },
    { field: 'block', headerName: t('firmware.columnBlock'), width: 90, type: 'number' },
    { field: 'retries', headerName: t('firmware.columnRetries'), width: 90, type: 'number' },
    {
      field: 'last_segment_sent_time',
      headerName: t('firmware.columnLastUpdate'),
      width: 180,
      valueGetter: value => formatTs(value),
    },
  ];
}

const FirmwareUpgradeBatchDetailPage = props => {
  const { batchId } = useParams();
  const { t } = useTranslation();
  const { batch, error, connected, lastEventAt, refetch } = useUpgradeBatchSocket(batchId);
  const columns = useMemo(() => getColumns(t), [t]);

  const [cancelOpen, setCancelOpen] = useState(false);
  const [cancelInFlight, setCancelInFlight] = useState(false);
  const [cancelError, setCancelError] = useState(null);

  const units = batch?.units || [];
  const fwLabel = batch?.firmware_version ? `v${batch.firmware_version.version}` : '—';
  // Cancel is allowed for anything still in flight (PENDING/RUNNING).
  // BATCH_CANCEL_DISABLED includes the CANCELLING transient too — once
  // the user hits cancel we hide the button until reconcile lands.
  const cancelDisabled = !batch || BATCH_CANCEL_DISABLED.has(batch.status);

  const handleCancelConfirm = async () => {
    setCancelInFlight(true);
    setCancelError(null);
    try {
      await cancelFirmwareUpgradeBatch(batchId);
      setCancelOpen(false);
      await refetch();
    } catch (err) {
      // Server returns 409 with code "batch_not_cancellable" when the
      // batch has already terminalised between confirm-shown and POST
      // (e.g., reaper fired). Surface the message; the refetch will
      // catch the page up.
      const detail = err?.response?.data?.detail || err?.message || t('firmware.cancelFailed');
      setCancelError(detail);
      await refetch();
    } finally {
      setCancelInFlight(false);
    }
  };

  return (
    <AppNav page="admin_firmware_upgrade_detail" notFullHeight {...props}>
      <Box sx={{ p: 2 }}>
        <TitleBar
          title={t('firmware.batchTitle', { fwLabel })}
          actions={
            <Button
              variant="outlined"
              color="error"
              size="small"
              disabled={cancelDisabled}
              onClick={() => {
                setCancelError(null);
                setCancelOpen(true);
              }}
            >
              {t('firmware.cancelBatch')}
            </Button>
          }
        />

        {error && (
          <Alert severity="error" sx={{ my: 1 }}>
            {t('firmware.loadBatchError', { message: error.message || String(error) })}
          </Alert>
        )}

        <Card sx={{ my: 2 }}>
          <CardContent>
            <Grid container spacing={2}>
              <Grid size={{ xs: 6, md: 3 }}>
                <Typography variant="caption" color="text.secondary">
                  {t('general.status')}
                </Typography>
                <Box>{batch ? <StatusChip label={batch.status} color={statusFamily(batch.status)} /> : '—'}</Box>
              </Grid>
              <Grid size={{ xs: 6, md: 3 }}>
                <Typography variant="caption" color="text.secondary">
                  {t('firmware.units')}
                </Typography>
                <Typography>{units.length}</Typography>
              </Grid>
              <Grid size={{ xs: 6, md: 3 }}>
                <Typography variant="caption" color="text.secondary">
                  {t('firmware.requested')}
                </Typography>
                <Typography>{formatTs(batch?.requested_at)}</Typography>
              </Grid>
              <Grid size={{ xs: 6, md: 3 }}>
                <Typography variant="caption" color="text.secondary">
                  {t('firmware.liveUpdates')}
                </Typography>
                <Stack direction="row" spacing={1} alignItems="center">
                  {connected ? (
                    <StatusChip color="ok" label={t('firmware.liveUpdatesSocket')} />
                  ) : (
                    <Tooltip title={t('firmware.liveUpdatesPollingTooltip')}>
                      <StatusChip color="neutral" label={t('firmware.liveUpdatesPolling')} />
                    </Tooltip>
                  )}
                  {lastEventAt && (
                    <Typography variant="caption" color="text.secondary">
                      {t('firmware.lastEventAt', { time: formatTs(lastEventAt) })}
                    </Typography>
                  )}
                </Stack>
              </Grid>
              {batch?.unresolved_iccids?.length ? (
                <Grid size={12}>
                  <Alert severity="warning">
                    {t('firmware.unresolvedIdentifiers', { count: batch.unresolved_iccids.length })}&nbsp;
                    <Box component="code" sx={{ wordBreak: 'break-all' }}>
                      {batch.unresolved_iccids.join(', ')}
                    </Box>
                  </Alert>
                </Grid>
              ) : null}
              {batch?.notes ? (
                <Grid size={12}>
                  <Typography variant="caption" color="text.secondary">
                    {t('firmware.notes')}
                  </Typography>
                  <Typography>{batch.notes}</Typography>
                </Grid>
              ) : null}
            </Grid>
          </CardContent>
        </Card>

        <Box sx={{ height: 'calc(100vh - 360px)', minHeight: 360 }}>
          <DataGridPro
            rows={units}
            columns={columns}
            loading={!batch && !error}
            getRowId={row => row.id}
            disableRowSelectionOnClick
          />
        </Box>

        <Dialog open={cancelOpen} onClose={() => !cancelInFlight && setCancelOpen(false)}>
          <DialogTitle>{t('firmware.cancelDialogTitle')}</DialogTitle>
          <DialogContent>
            <DialogContentText sx={{ mb: 1 }}>
              {t('firmware.cancelDialogBody')} <strong>{t('firmware.cancelledStatus')}</strong>.
            </DialogContentText>
            <DialogContentText component="div">
              <Alert severity="warning" sx={{ mt: 1 }}>
                {t('firmware.cancelDialogDaemonWarning')}
              </Alert>
            </DialogContentText>
            {cancelError && (
              <Alert severity="error" sx={{ mt: 2 }}>
                {cancelError}
              </Alert>
            )}
          </DialogContent>
          <DialogActions>
            <Button onClick={() => setCancelOpen(false)} disabled={cancelInFlight}>
              {t('firmware.keepGoing')}
            </Button>
            <Button
              onClick={handleCancelConfirm}
              color="error"
              variant="contained"
              disabled={cancelInFlight}
              startIcon={cancelInFlight ? <CircularProgress size={16} color="inherit" /> : null}
            >
              {t('firmware.cancelBatch')}
            </Button>
          </DialogActions>
        </Dialog>
      </Box>
    </AppNav>
  );
};

export default FirmwareUpgradeBatchDetailPage;
